import { useEffect } from 'react';
import type { Me } from '../domain/account';
import { useLoad } from './useLoad';
import { useRepositories } from './useRepositories';

const ACCOUNT_CHANGED = 'marketplace:account-changed';

/**
 * Tell every `useMe()` on the page that the account changed (a value was
 * verified or removed), so things derived from it — the unverified banner —
 * catch up without a reload.
 */
export function announceAccountChanged(): void {
  window.dispatchEvent(new Event(ACCOUNT_CHANGED));
}

/** Hear every `announceAccountChanged()`; returns the unsubscribe. */
export function subscribeAccountChanged(listener: () => void): () => void {
  window.addEventListener(ACCOUNT_CHANGED, listener);
  return () => window.removeEventListener(ACCOUNT_CHANGED, listener);
}

/** The signed-in account, re-read whenever it's announced as changed; null when signed out. */
export function useMe() {
  const { account, session } = useRepositories();
  const signedIn = !!session.current().user?.isAuthenticated;
  const me = useLoad<Me | null>(
    () => (signedIn ? account.me() : Promise.resolve(null)),
    signedIn ? 'me' : 'signed-out',
  );
  const { refresh } = me;

  useEffect(() => subscribeAccountChanged(refresh), [refresh]);

  return me;
}
